'use client';

import React, { useState, useRef, useEffect } from 'react';

interface ShowMoreTextProps {
  /** The text content to display */
  text: string;
  /**
   * Render `text` as HTML instead of plain text. The caller MUST pass
   * pre-sanitized markup (see app/lib/richText.ts sanitizeDescriptionHtml) —
   * this component does no sanitizing of its own. Used for US (RIDB)
   * descriptions which arrive as HTML fragments; the plain-text path is
   * unchanged.
   */
  asHtml?: boolean;
  /** Maximum number of lines before truncation (default: 4) */
  maxLines?: number;
  /** Custom class name for the text container */
  className?: string;
  /** Label for the "show more" button (default: "Show more") */
  showMoreLabel?: string;
  /** Label for the "show less" button (default: "Show less") */
  showLessLabel?: string;
}

// Prose styling for the HTML path — headings kept modest so RIDB's
// <h2>Overview</h2> sub-sections read as part of the ~15px body copy,
// not competing with the page's section headings.
const HTML_PROSE_CLASSES = [
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-5 [&_h2]:mb-2 [&_h2:first-child]:mt-0',
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3:first-child]:mt-0',
  '[&_h4]:text-[15px] [&_h4]:font-semibold [&_h4]:text-slate-800 [&_h4]:mt-3 [&_h4]:mb-1 [&_h4:first-child]:mt-0',
  '[&_p]:mt-0 [&_p]:mb-3 [&_p:last-child]:mb-0',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-3',
  '[&_li]:mb-1',
].join(' ');

export default function ShowMoreText({
  text,
  asHtml = false,
  maxLines = 4,
  className = '',
  showMoreLabel = 'Show more',
  showLessLabel = 'Show less',
}: ShowMoreTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    // Check if the text overflows the clamped height
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 24;
    const maxHeight = lineHeight * maxLines;
    setIsTruncated(el.scrollHeight > maxHeight + 2); // +2 for rounding
  }, [text, maxLines]);

  const clampStyle =
    !isExpanded && isTruncated
      ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }
      : undefined;

  return (
    <div>
      {asHtml ? (
        <div
          ref={textRef}
          className={`text-gray-700 leading-relaxed ${HTML_PROSE_CLASSES} ${className}`}
          style={clampStyle}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : (
        <div
          ref={textRef}
          className={`text-gray-700 whitespace-pre-line leading-relaxed ${className}`}
          style={clampStyle}
        >
          {text}
        </div>
      )}

      {isTruncated && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-gray-900 font-semibold text-sm underline underline-offset-4 decoration-1 hover:text-black transition-colors cursor-pointer"
        >
          {isExpanded ? showLessLabel : showMoreLabel} ›
        </button>
      )}
    </div>
  );
}
